import type { LearnTopic } from "../topic-types";

/**
 * Hypervigilance — Learn Hub topic page.
 *
 * Educational, non-diagnostic content about hypervigilance: what it is,
 * how it shows up, and what tends to help the nervous system settle.
 */
export const HYPERVIGILANCE_TOPIC: LearnTopic = {
  slug: "hypervigilance",
  emoji: "👁️",
  title: "Hypervigilance",
  tagline: "When your body stays on watch long after the danger has passed",
  theme: "sky",
  category: "nervous-system",
  isNew: true,
  seoDescription:
    "A trauma-informed, plain-English guide to hypervigilance — what it is, why the body stays on alert, how it shows up day to day, and gentle practices that help the nervous system settle. From Recovery Trauma™.",
  cardBlurb:
    "Understand why your body stays on alert, how hypervigilance shows up, and what helps it settle.",
  intro: [
    "Hypervigilance is a state of heightened alertness — the nervous system scanning constantly for signs of danger, even in situations that are objectively safe. It can feel like never quite being able to relax, noticing everything at once, or reading a room before you have even sat down.",
    "It is not paranoia, and it is not a character flaw. It is a protective pattern: a system that once needed to detect threat quickly, still doing that job faithfully long after the threat has gone.",
    "This guide moves from understanding, to recognising your own pattern, to practical ways of helping the body come down from high alert. Take what is useful and leave the rest.",
  ],
  closing:
    "Hypervigilance usually eases slowly, in small increments, as the body gathers repeated evidence of safety. If it is constant, exhausting or affecting sleep and daily life, working alongside a trauma-informed therapist can make this feel far more manageable.",
  continueTo: [
    { label: "Grounding techniques", to: "/learn/grounding-techniques", note: "Practices for coming back to the present" },
    { label: "Understanding your triggers", to: "/learn/triggers" },
    { label: "Trauma responses", to: "/learn/trauma-responses" },
    { label: "Hypervigilance — full article", to: "/blog/hypervigilance-why-you-always-feel-on-edge" },
  ],
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What hypervigilance is, and why the body keeps watch.",
      intro: "Education first — nothing here asks you to look inward yet.",
      resources: [
        {
          slug: "what-is-hypervigilance",
          kind: "learn",
          title: "What is hypervigilance?",
          summary: "A nervous system holding the alarm switch down.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Hypervigilance is sustained, elevated threat-detection. The brain's alarm system stays partly switched on, so ordinary cues — a door closing, a change of tone, an unread message — get processed as possible danger before the thinking mind has a say.",
            },
            {
              type: "list",
              title: "Ideas people often find helpful",
              items: [
                "It is a protective adaptation, not an overreaction",
                "It usually develops where being alert genuinely kept you safer",
                "It costs energy — which is why exhaustion so often travels with it",
                "It can soften, gradually, as the body collects evidence of safety",
              ],
            },
            {
              type: "note",
              text: "This page is educational, not a diagnosis. Only a qualified clinician can assess what is happening for you.",
            },
          ],
        },
        {
          slug: "how-it-shows-up",
          kind: "learn",
          title: "How it shows up day to day",
          summary: "In the body, in attention, in relationships.",
          minutes: 3,
          blocks: [
            {
              type: "list",
              title: "In the body",
              items: [
                "Shallow, high breathing; a tight jaw, chest or shoulders",
                "Startling easily at sudden sounds or movement",
                "Trouble falling asleep, or waking at the smallest noise",
                "Tiredness that rest does not seem to fix",
              ],
            },
            {
              type: "list",
              title: "In attention and thinking",
              items: [
                "Scanning faces, exits, tone of voice, small changes in mood",
                "Difficulty concentrating because part of you is on watch",
                "Rehearsing what could go wrong so you are never caught off guard",
              ],
            },
            {
              type: "list",
              title: "In relationships",
              items: [
                "Reading meaning into small shifts in someone's warmth",
                "Feeling unable to fully settle even with people you trust",
                "Anticipating other people's needs before your own register",
              ],
            },
          ],
        },
        {
          slug: "why-it-persists",
          kind: "learn",
          title: "Why it persists after the danger has passed",
          summary: "Learning is fast; unlearning is slow — and that is normal.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Threat learning is designed to be fast and durable — one frightening experience can be enough. Safety learning works differently: it needs repetition, over time, in the body rather than only in thought. That mismatch is why knowing you are safe often does not, on its own, feel like being safe.",
            },
            {
              type: "flow",
              title: "The usual sequence",
              items: ["Cue noticed", "Alarm fires before thought", "Body mobilises", "Thinking mind catches up", "Slow settling"],
            },
            {
              type: "quote",
              text: "The goal is not to stop noticing. It is to be able to notice, check, and come back down.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔍",
      title: "Recognise your pattern",
      blurb: "Map your own early signs, cues and after-effects.",
      resources: [
        {
          slug: "early-warning-signs",
          kind: "reflect",
          title: "Your early warning signs",
          summary: "Catching activation earlier gives you more choice.",
          minutes: 6,
          blocks: [
            {
              type: "prompts",
              title: "Gentle prompts",
              items: [
                "Where in my body do I first notice I have gone on alert?",
                "What kinds of situations most often set it off?",
                "What do I do when I am scanning — and what do I stop doing?",
                "What helps me come back down, even a little?",
                "Who or where do I feel most able to unclench around?",
              ],
            },
            {
              type: "note",
              text: "If a prompt feels like too much right now, skip it. Pacing is part of the work, not a failure of it.",
            },
          ],
        },
        {
          slug: "alertness-log",
          kind: "worksheet",
          title: "Alertness log",
          summary: "A simple printable log for spotting patterns over a week.",
          worksheetFields: [
            { label: "Day and time", lines: 1 },
            { label: "What was happening", lines: 2 },
            { label: "First body signal I noticed", lines: 2 },
            { label: "How alert I felt (1–10)", lines: 1 },
            { label: "What helped, if anything", lines: 2 },
            { label: "How long until I settled", lines: 1 },
          ],
        },
      ],
    },
    {
      slug: "what-helps",
      emoji: "🌿",
      title: "What helps",
      blurb: "Practices that support the body in coming down from high alert.",
      resources: [
        {
          slug: "orienting",
          kind: "practice",
          title: "Orienting to the room",
          summary: "Letting the eyes finish the scan on purpose, slowly.",
          minutes: 4,
          blocks: [
            {
              type: "steps",
              title: "Try this",
              items: [
                { label: "Let your eyes move slowly around the space, without hurrying" },
                { label: "Notice three things that are simply neutral — a wall, a chair, a shadow" },
                { label: "Let your head turn with your eyes, rather than holding it still" },
                { label: "Notice the exits, the doors, the light — deliberately, not anxiously", note: "This tells the body the scan is complete." },
                { label: "Pause. Notice one breath out that is a little longer than the one in" },
              ],
            },
            {
              type: "note",
              text: "If this increases alertness rather than easing it, stop and try something with movement instead.",
            },
          ],
        },
        {
          slug: "longer-out-breath",
          kind: "practice",
          title: "The longer out-breath",
          summary: "A small, portable way to signal safety to the body.",
          minutes: 3,
          blocks: [
            {
              type: "steps",
              title: "Try this",
              items: [
                { label: "Breathe in gently through the nose for a count of four" },
                { label: "Let the out-breath be longer — six or eight, without forcing" },
                { label: "Repeat five or six times, then stop and notice" },
              ],
            },
            {
              type: "prose",
              text: "A longer out-breath supports the branch of the nervous system associated with settling. It rarely produces instant calm — it nudges the system in the right direction.",
            },
            { type: "cta", text: "More practices for coming back to the present", label: "Open grounding techniques", to: "/learn/grounding-techniques" },
          ],
        },
        {
          slug: "building-safety-evidence",
          kind: "reflect",
          title: "Building evidence of safety",
          summary: "Small, repeated moments matter more than big reassurance.",
          minutes: 5,
          blocks: [
            {
              type: "list",
              title: "Ways people build this",
              items: [
                "Naming, at the end of the day, one moment where nothing bad happened",
                "Spending predictable time in one place that feels reliably okay",
                "Keeping company with people whose mood does not swing unpredictably",
                "Letting the body finish a stress cycle through movement, warmth or shaking it out",
              ],
            },
            {
              type: "quote",
              text: "Safety is not decided once. It is deposited, in small amounts, over time.",
            },
          ],
        },
      ],
    },
  ],
};
